import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { ITagProps } from 'libs/design-system/src/types/tag/tag';
import { Button } from '@unified-checkout/design-system';
import {
  Copy,
  Print,
} from '@unified-checkout/design-system/icons/index';

import type { TCreatePaymentLinkPayload } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link/create_payment';
import type { IFooterProps } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import ShareModal from '@app/dashboard/components/shared/modals/ShareModal';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { useQRCode } from '@app/dashboard/hooks/useQRCode';
import { useUserInfoStore } from '@app/dashboard/store/userInfo';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import {
  CUSTOMER_DETAILS_TYPES,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';
import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
  SINGLE_PAYMENT_LINKS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/payment_links/single';
import { shareSinglePaymentLink } from '@app/dashboard/services/inner_layout/single_payment_links/share-with';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { createSinglePaymentLink } from '@app/dashboard/services/inner_layout/single_payment_links/create-payment';
import { useGoBack } from '@app/dashboard/hooks/useGoBack';
import { notify } from '@app/dashboard/utils/notifier';

import { formatPayload, isFormHaveErrors, payloadToFormData } from './utils';

import styles from './footer.module.scss';

const Footer = ({
  fields,
  tags,
  fullPageStatus,
  setFullPageStatus,
  customerDetailsType,
}: Omit<IFooterProps, 'setFields'>) => {
  const goBack = useGoBack();
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const navigate = useNavigate();
  const { userInfo: { profileType } } = useUserInfoStore();  

  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const {
    printModal,
    copyModalToClipboard,
    isLoading: isQRCodeLoading,
  } = useQRCode(modalBodyRef);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState({ id: '', shortenUrl: '' });
  const [restTags, setRestTags] = useState(0);

  const hasSharePermission = isAccessible(PERMISSIONS.CAN_SHARE_QUICK_LINKS);
  const canShareQuickLinks = profileType === PROFILE.TYPE.MERCHANT || (profileType === PROFILE.TYPE.MERCHANT_STAFF && hasSharePermission);


  const isConfirmDisabled = useMemo(() => {
    const amountField = fields[SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]?.value;
    const paymentMethods =
      fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS]?.value;
    const email = fields[SINGLE_PAYMENTS_FIELDS.EMAIL];
    const phone = fields[SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER];
    const date = fields[SINGLE_PAYMENTS_FIELDS.DATE];

    let isContactMissing =
      customerDetailsType === CUSTOMER_DETAILS_TYPES.ONE &&
      email && !email.value &&
      phone && !phone.value;
      
    const isDateMissing = date && !date?.value;
    const hasErrors = isFormHaveErrors(fields);
    const isLoading = fullPageStatus.loading;

    return (
      hasErrors ||
      isLoading ||
      !amountField ||
      !paymentMethods ||
      isContactMissing ||
      isDateMissing
    );
  }, [fields, fullPageStatus.loading, customerDetailsType]);

  const { isPending: isPendingCreate, mutate: mutateCreatePaymentLink } =
    useMutation({
      mutationFn: (payload: FormData) => createSinglePaymentLink(payload),
      onSuccess: (res) => {
        setPaymentLink({
          shortenUrl: res.data.shorten_url,
          id: res.data.id,
        });
        amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.PAYMENT_LINK_CREATED, {
          [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.LINK_ID]: res.data.id,
          [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
        });
        setIsShareModalOpen(true);
      },

      onError: (
        axiosErr: AxiosError<{
          message?: string;
          errors?: Record<string, string>;
        }>
      ) => {
        let message = t('errors.somethingWentWrong');
        const isValidationError = [422, 400].includes(
          axiosErr.response?.status || 0
        );

        const errors = axiosErr.response?.data?.errors;
        const errorsField = errors ? Object.keys(errors).join('| ') : undefined;

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response.data.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        amplitude.track(
          SINGLE_PAYMENT_LINKS_EVENTS.PAYMENT_LINK_CREATION_FAILED,
          {
            [SHARED_EVENTS_PARAMS.FAILURE_REASON]: isValidationError
              ? SINGLE_PAYMENT_LINKS_EVENTS_VALUES.VALIDATION_ERROR
              : SINGLE_PAYMENT_LINKS_EVENTS_VALUES.API_ERROR,
            [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: message,
            [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.ERR_FIELD]: errorsField,
          }
        );  
        notify.error(message, { isCreatePortal: !isShareModalOpen });
      },
    });

  const { mutateAsync: mutateSharePaymentLink, isPending: isPendingShare } =
    useMutation({
      mutationFn: (payload: {
        share_with: string[];
        multiple_link_id: string;
      }) => shareSinglePaymentLink(payload),
      onSuccess: () => {
        notify.success(t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.shareModal.toaster.sharedSuccess'
        ), { isCreatePortal: !isShareModalOpen });
        setRestTags((prev) => prev + 1);
      },
      onError: (axiosErr: AxiosError<{ message?: string }>) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response.data.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        notify.error(message, { isCreatePortal: !isShareModalOpen });
      },
    });

  const handleConfirm = useCallback(() => {
    const result = formatPayload({
      fields,
      tags: tags.map((tag) => tag.text),
      customerType: customerDetailsType,
    }) as TCreatePaymentLinkPayload;

    const formData = payloadToFormData({ ...result, is_live: isLive });

    mutateCreatePaymentLink(formData);
  }, [isLive, tags, fields, customerDetailsType]);

  const getShareLinkTrackingPayload = (contacts: ITagProps[]) => {
    const recipientTypes: string[] = [];

    for (const contact of contacts) {
      const type = contact.text.includes('@') ? 'email' : 'phone';

      if (!recipientTypes.includes(type)) {
        recipientTypes.push(type);
      }
      if (recipientTypes.length === 2) break;
    }

    return {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.RECIPIENTS_COUNT]: contacts.length,
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.RECIPIENT_TYPES]: recipientTypes,
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.EGYPT_ONLY_SMS_CONSTRAINT]: true,
    };
  };

  const handleShareContacts = async (contacts: ITagProps[]) => {
    if (!contacts.length || !paymentLink.id) return;

    const payload = {
      share_with: contacts.map((tag) => tag.text),
      multiple_link_id: paymentLink.id,
    };

    try {
      await mutateSharePaymentLink(payload);
      amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.LINK_SHARED, {
        ...getShareLinkTrackingPayload(contacts),
      });
    } catch (err) {}
  };

  const handleCloseShareModal = () => {
    if (!paymentLink.id) return;

    navigate(INNER_ROUTES.QUICK_LINKS);
  };

  const handleCopyText = (text: string) => {
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.LINK_COPIED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.COPY_TARGET]: text,
    });
  };

  const handleQRCodeOpenChange = (isOpen: boolean) => {
    if (!isOpen) return;

    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.QR_CODE_VIEWED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.SOURCE]:
        SINGLE_PAYMENT_LINKS_EVENTS_VALUES.PAYMENT_LINK_SHARE_MODAL,
    });
  };

  const handlePrintQRCode = () => {
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.QR_CODE_PRINTED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.SOURCE]:
        SINGLE_PAYMENT_LINKS_EVENTS_VALUES.PAYMENT_LINK_QR_MODAL,
    });

    printModal();
  };

  const handleCopyQRCode = () => {
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.QR_CODE_COPIED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.COPY_TARGET]:
        SINGLE_PAYMENT_LINKS_EVENTS_VALUES.QR_CODE_IMG,
    });

    copyModalToClipboard().then(() => {
      notify.success(t('copied'));
    });
  };

  const FOOTER_ACTIONS: IButton[] = useMemo(
    () => [
      {
        size: 'lg',
        color: 'secondary',
        variant: 'outlined',
        text: t('form.cancel'),
        onClickHandler: () => goBack(),
        disabled: isPendingCreate,
      },
      {
        size: 'lg',
        color: 'primary',
        variant: 'contained',
        text: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.actions.createPaymentLink'
        ),
        onClickHandler: handleConfirm,
        disabled: isConfirmDisabled || isPendingCreate,
      },
    ],
    [t, fields, isPendingCreate, tags]
  );

  useEffect(() => {
    setFullPageStatus((prev) => ({ ...prev, disabled: isPendingCreate }));
  }, [isPendingCreate]);

  useEffect(() => {
    if (isShareModalOpen) {
      amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.SHARE_MODAL_VIEWED, {
        [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
      });
    }
  }, [isShareModalOpen]);

  return (
    <div className={styles['footer']}>
      {FOOTER_ACTIONS.map((btn, index) => (
        <Button key={`single-ql-footer-${index}`} {...btn} />
      ))}

      <ShareModal
        modalProps={{
          isModalOpen: isShareModalOpen,
          isOverlayAboveModal: true,
          hasCloseIcon: true,
          handleCloseModal: handleCloseShareModal,
          title: t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.shareModal.title'
          ),
          subtitle: t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.shareModal.subTitle'
          ),
          closeOnClickOutside: true,
        }}
        shareInputProps={{
          link: paymentLink.shortenUrl,
          label: t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.shareModal.actions.shareLink'
          ),
        }}
        inputTagsProps={{
          showTagsInput: canShareQuickLinks,
          defaultTags: tags,
          tagsShareBtn: {
            onClickHandler: (tags: ITagProps[]) => handleShareContacts(tags),
            loading: isPendingShare,
            disabled: isPendingShare,
          },
          resetTagsOnShare: restTags,
        }}
        QRCodeProps={{
          onQRCodeOpenChange: handleQRCodeOpenChange,
          modalBodyRef: modalBodyRef,
          modalProps: {
            closeOnClickOutside: true,
            isModalOpen: true,
            handleCloseModal: () => {},
            footerBtn: [
              {
                text: t(
                  'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.qrCode.actions.copy'
                ),
                variant: 'contained',
                color: 'primary',
                size: 'md',
                leadingIcon: Copy,
                onClickHandler: handleCopyQRCode,
                loading: isQRCodeLoading,
                disabled: isQRCodeLoading,
              },
              {
                text: t(
                  'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.qrCode.actions.print'
                ),
                variant: 'outlined',
                color: 'secondary',
                size: 'md',
                leadingIcon: Print,
                onClickHandler: handlePrintQRCode,
              },
            ],
          },
          cardProps: {
            showPaymobTheme: true,
            // cardTitle: companyName,
            // cardLogo: logo || '',
          },
        }}
        onCopyTextHandler={handleCopyText}
      />
      
    </div>
  );
};

export default Footer;
